import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, MapPin, Star } from "lucide-react";
import { scrollToId } from "@/lib/smooth";

const SLIDES = [
  { src: "/photos/ponte.jpg", caption: "Ponte delle Torri · Spoleto" },
  { src: "/photos/cerreto.jpg", caption: "Borghi e colline della Valnerina" },
  { src: "/photos/duomo.jpg", caption: "Piazza del Duomo · Spoleto" },
];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-stone-900">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        {SLIDES.map((s, i) => (
          <motion.img
            key={s.src}
            src={s.src}
            alt={s.caption}
            initial={false}
            animate={{ opacity: i === slide ? 1 : 0 }}
            transition={{ duration: 1.8, ease: "easeInOut" }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/25 to-stone-950/10" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-6 flex flex-wrap items-center gap-3"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-linen/25 bg-linen/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-linen/90 backdrop-blur-md">
            <MapPin size={12} className="text-oro" /> Eggi di Spoleto · Umbria
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-linen/25 bg-linen/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-linen/90 backdrop-blur-md">
            Romantic Tiny House
          </span>
        </motion.div>

        <h1 className="font-serif text-linen">
          <MaskedLine delay={0.25} className="text-5xl leading-[1.02] tracking-tight sm:text-7xl lg:text-8xl">
            Saint Anthony
          </MaskedLine>
          <MaskedLine delay={0.4} className="mt-2 text-2xl italic leading-snug text-oro sm:text-4xl lg:text-5xl">
            un nido d'amore tra le mura del borgo
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-7 max-w-xl text-base font-light leading-relaxed text-linen/85 sm:text-lg"
        >
          Tranquilla, accogliente, decisamente riservata: una tiny house ricavata dentro un antico
          edificio centenario, con giardino privato tra mura secolari e piscina SPA riscaldata —
          nel cuore del borgo di Eggi, a 6 km da Spoleto.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-cta"
            onClick={() => scrollToId("prenota")}
            className="group rounded-full bg-terracotta px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-linen transition-transform duration-300 hover:scale-[1.04] active:scale-95"
          >
            Prenota il tuo soggiorno
          </button>
          <button
            data-testid="hero-discover-cta"
            onClick={() => scrollToId("dimora")}
            className="rounded-full border border-linen/40 px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-linen backdrop-blur-sm transition-colors duration-300 hover:bg-linen/10"
          >
            Scopri la casa
          </button>
          <span className="ml-1 inline-flex items-center gap-2 text-sm text-linen/80">
            <Star size={15} className="fill-oro text-oro" /> 8,2 su Booking · coppie 9,2
          </span>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("dimora")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-6 z-10 hidden text-linen/70 transition-colors hover:text-oro sm:block"
        aria-label="Scorri"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ChevronDown size={28} />
        </motion.div>
      </motion.button>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 right-6 z-10 hidden flex-col items-end gap-3 sm:flex"
      >
        <AnimatePresence mode="wait">
          <motion.p
            key={slide}
            data-testid="hero-slide-caption"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.5 }}
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-linen/70"
          >
            {SLIDES[slide].caption}
          </motion.p>
        </AnimatePresence>
        <div className="flex gap-1.5">
          {SLIDES.map((s, i) => (
            <button
              key={s.src}
              data-testid={`hero-slide-dot-${i}`}
              onClick={() => setSlide(i)}
              className={`h-1 rounded-full transition-[width,background-color] duration-500 ${i === slide ? "w-6 bg-oro" : "w-2 bg-linen/40"}`}
              aria-label={`Panoramica ${i + 1}`}
            />
          ))}
        </div>
      </motion.div>
    </section>
  );
}
